import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { map } from 'rxjs';
import { QueryRef } from 'apollo-angular';

import { AuthService } from '@core/services/auth/auth.service';
import { NavigationMenuItem, NavigationService } from '@core/services/navigation.service';
import { MeGQL, MeQuery } from '@generated/graphql';
import {
  PalantirSideMenuComponent,
  SideMenuItem,
  SideMenuUser,
  SideMenuCompany,
} from '@flux/design-system';
import { MySpaceBadgesService } from '@pages/my-space/my-space-badges.service';
import { SidebarService } from './sidebar.service';
import { PermissionsService } from '../../../core/services/permissions.service.js';
import { FeatureFlagService } from '../../../core/services/feature-flag/feature-flag.service.js';
import { FeatureFlagsEnum } from '../../../../generated/featureFlags.enum.js';
import { PermissionsEnum } from '../../../../generated/permissions.enum.js';
import { BlogNotificationService } from '../../../core/services/blog-notification/blog-notification.service.js';
import { sharedLogger } from '../../logger.instance.js';
import { DelegationIndicatorComponent } from '../../components/delegation-indicator/delegation-indicator.component.js';

const sidebarLogger = sharedLogger.extend('sidebar');

@Component({
  selector: 'fx-sidebar',
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PalantirSideMenuComponent, DelegationIndicatorComponent],
  providers: [SidebarService],
})
export class SidebarComponent implements OnInit, OnDestroy {
  public readonly blogNotificationService = inject(BlogNotificationService);

  private readonly user = signal<MeQuery['me'] | null>(null);
  private readonly notificationsCount = signal(0);
  private queryRef!: QueryRef<MeQuery>;

  public isCollapsed = this.sidebarService.collapsed;

  /**
   * User info for the side menu
   */
  public sideMenuUser = computed<SideMenuUser>(() => {
    const user = this.user();
    return {
      name: user?.name ?? '',
      email: user?.email ?? '',
      image: user?.avatarUrl ?? 'assets/favicon.svg',
    };
  });

  /**
   * Company info for the side menu (hardcoded to Flux Control)
   */
  public sideMenuCompany = computed<SideMenuCompany>(() => ({
    name: '',
    image: {
      expanded: 'assets/logo/flux-logo.svg',
      collapsed: 'assets/favicon.svg',
    },
  }));

  /**
   * Notification count for the side menu badge
   */
  public notificationCount = computed(() => this.notificationsCount());

  /**
   * Menu items filtered by permissions and feature flags, with badges applied
   */
  public menuItems = computed<SideMenuItem[]>(() => {
    const navItems = this.navigationService.getNavigationItems();
    const notificationCount = this.notificationsCount();
    const hasUnreadBadges = this.mySpaceBadgesService.hasUnreadBadges();
    // Subscreve no signal de flags para que a computed reaja quando o fetch
    // inicial popular o `featureFlagMap` (que é uma Map imperativa, não-reativa).
    this.featureFlagsService.featureFlags();

    return this.processNavigationItems(navItems, notificationCount, hasUnreadBadges);
  });

  public canShowBlogNotifications = signal(false);

  constructor(
    private readonly sidebarService: SidebarService,
    private readonly authService: AuthService,
    private readonly destroyRef: DestroyRef,
    private readonly navigationService: NavigationService,
    private readonly permissionsService: PermissionsService,
    private readonly featureFlagsService: FeatureFlagService,
    private readonly mySpaceBadgesService: MySpaceBadgesService,
    private readonly meGQL: MeGQL,
    private readonly router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    this.sidebarService.notificationsCount$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        sidebarLogger.debug('Received notifications count update:', value);
        this.notificationsCount.set(value);
      });

    this.fetchUser();

    this.canShowBlogNotifications.set(
      await this.featureFlagsService.isFeatureFlagEnabled(FeatureFlagsEnum.FeatureBlogPosts),
    );
  }

  ngOnDestroy(): void {
    if (this.queryRef) {
      this.queryRef.stopPolling();
    }
  }

  /**
   * Handle menu item click - navigate to the item's URL
   */
  public onMenuItemClick(item: SideMenuItem): void {
    if (item.url) {
      sidebarLogger.debug('Navigating to menu item:', item.url);
      this.router.navigate([item.url]);
    }
  }

  /**
   * Handle user action click (logout, profile)
   */
  public onUserAction(action: string): void {
    sidebarLogger.debug('User action triggered:', action);
    switch (action) {
      case 'logout':
        this.authService.logout();
        break;
      case 'profile':
        this.router.navigate(['perfil']);
        break;
    }
  }

  public onHelpButtonClick(): void {
    window.open('/wiki/index.html', '_blank');
  }

  public onNotificationButtonClick(): void {
    this.router.navigate(['notificacoes']);
  }

  public onRocketButtonClick(): void {
    if (!this.featureFlagsService.isFeatureFlagEnabled(FeatureFlagsEnum.FeatureBlogPosts)) {
      sidebarLogger.warn('Blog posts feature is disabled. Cannot open blog notifications.');
      return;
    }
    this.blogNotificationService.openManuallyWithAllPosts();
  }

  /**
   * Toggle sidebar collapsed state
   */
  public onToggleMenu(): void {
    this.sidebarService.toggleCollapse();
  }

  /**
   * Recursively process navigation items: filter by permissions and apply badges
   */
  private processNavigationItems(
    items: NavigationMenuItem[],
    notificationCount: number,
    hasUnreadBadges: boolean,
  ): SideMenuItem[] {
    return items.map((item) => this.transformItem(item, notificationCount, hasUnreadBadges));
  }

  /**
   * Visibilidade do item: AND entre flags e permissões. Todas as flags
   * precisam estar ativas; basta UMA das `required_permissions` para liberar
   * o item — o array representa alternativas equivalentes (ex.: `*ReadAll`
   * OU `*ReadLimited`, que são mutuamente exclusivas por papel).
   *
   * `isFeatureFlagEnabled` retorna Promise (sempre truthy), por isso leio o
   * `featureFlagMap` síncrono já populado no init.
   */
  private isItemVisible(item: NavigationMenuItem): boolean {
    if (item.has_feature_flag?.length) {
      const allFlagsEnabled = item.has_feature_flag.every(
        (flag) =>
          this.featureFlagsService.featureFlagMap.get(flag as FeatureFlagsEnum)?.active ?? false,
      );
      if (!allFlagsEnabled) {
        return false;
      }
    }

    if (item.required_permissions?.length) {
      const hasAnyPermission = item.required_permissions.some((permission) =>
        this.permissionsService.hasPermission(permission as PermissionsEnum),
      );
      if (!hasAnyPermission) {
        return false;
      }
    }

    return true;
  }

  /**
   * Transform a NavigationMenuItem to SideMenuItem, handling children recursively
   */
  private transformItem(
    item: NavigationMenuItem,
    notificationCount: number,
    hasUnreadBadges: boolean,
  ): SideMenuItem {
    // Create base menu item
    const menuItem: SideMenuItem = {
      id: item.id,
      title: item.title,
      url: item.url,
      hidden: !this.isItemVisible(item),
      icon: item.icon,
      withDivider: item.withDivider,
      dontAppearOnClosed: item.dontAppearOnClosed,
      isBeta: item.isBeta,
    };

    // Recursively process children if present
    if (item.children?.length) {
      const filteredChildren = this.processNavigationItems(
        item.children,
        notificationCount,
        hasUnreadBadges,
      );
      // Only include children array if there are visible items
      if (filteredChildren.length > 0) {
        menuItem.children = filteredChildren;
      }
    }

    // Apply badges for specific items
    if (item.id === 'notificacoes' && notificationCount > 0) {
      menuItem.badge = {
        title: notificationCount > 99 ? '99+' : notificationCount,
        bg: 'var(--flux-blue-500)',
      };
    }

    if (item.id === 'meu-espaco' && hasUnreadBadges) {
      menuItem.badge = {
        title: '●',
        bg: 'var(--flux-blue-500)',
      };
    }

    return menuItem;
  }

  private fetchUser(): void {
    this.queryRef = this.meGQL.watch();

    this.queryRef.valueChanges
      .pipe(
        map((result) => result.data.me),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => {
        this.user.set(data);
      });
  }
}
